import type { FC, ReactNode } from 'react'

import { ChartColumn } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDate } from '@/lib/dates'
import { EmptyState } from '@/components/ui/feedback'

export type TrendPoint = {
    date: string
    present: number
    total: number
    rate: number
}

const TICKS = [100, 75, 50, 25, 0]

// Single-series column chart: one hue, hairline grid, values above the columns,
// and the same numbers repeated by hover or keyboard focus and in "Riwayat absensi".
export const AttendanceTrend: FC<{ points: TrendPoint[] }> = ({ points }): ReactNode => {
    if (points.length === 0) {
        return (
            <EmptyState
                icon={<ChartColumn />}
                title='Grafik akan tampil setelah absensi dicatat.'
                description='Catat kehadiran siswa untuk mulai melihat tren.'
            />
        )
    }

    return (
        <div className='px-5 pt-9 pb-4'>
            <div className='flex gap-3'>
                <div className='relative h-48 w-9 shrink-0 text-[11px] text-muted-foreground tabular-nums' aria-hidden='true'>
                    {TICKS.map(tick => (
                        <span key={tick} className='absolute right-0 -translate-y-1/2' style={{ top: `${100 - tick}%` }}>
                            {tick}%
                        </span>
                    ))}
                </div>

                <div className='relative h-48 min-w-0 flex-1'>
                    {TICKS.map(tick => (
                        <div
                            key={tick}
                            className={cn('absolute inset-x-0 border-t', tick === 0 ? 'border-border' : 'border-border/60')}
                            style={{ top: `${100 - tick}%` }}
                            aria-hidden='true'
                        />
                    ))}

                    <ul className='relative flex h-full items-end justify-around gap-2 sm:gap-4'>
                        {points.map((point, index) => {
                            const latest = index === points.length - 1
                            const tooltipPosition = index === 0 ? 'left-0' : latest ? 'right-0' : 'left-1/2 -translate-x-1/2'

                            return (
                                <li
                                    key={point.date}
                                    tabIndex={0}
                                    className='group relative flex h-full w-full max-w-16 cursor-default flex-col items-center justify-end rounded-md outline-none focus-visible:ring-3 focus-visible:ring-ring/40'
                                >
                                    <span className='sr-only'>
                                        {`${formatDate(point.date, 'long')}: ${point.rate}% hadir, ${point.present} dari ${point.total} siswa tercatat.`}
                                    </span>

                                    <div
                                        className='relative w-full max-w-11 rounded-t-[4px] bg-primary transition-[background-color,height] duration-500 group-hover:bg-primary/85'
                                        style={{ height: `${Math.max(point.rate, 1.5)}%` }}
                                        aria-hidden='true'
                                    >
                                        <span
                                            className={cn(
                                                'absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-semibold tabular-nums group-hover:invisible group-focus-visible:invisible',
                                                latest ? 'text-foreground' : 'text-muted-foreground'
                                            )}
                                        >
                                            {point.rate}%
                                        </span>

                                        <span
                                            className={cn(
                                                'pointer-events-none absolute bottom-full z-10 mb-2 hidden w-max rounded-lg border bg-popover px-3 py-2 text-left shadow-float group-hover:block group-focus-visible:block',
                                                tooltipPosition
                                            )}
                                        >
                                            <span className='block text-xs font-medium text-muted-foreground'>
                                                {formatDate(point.date, 'long')}
                                            </span>

                                            <span className='block text-sm font-semibold text-foreground'>
                                                {point.rate}% hadir
                                            </span>

                                            <span className='block text-xs text-muted-foreground'>
                                                {point.present} dari {point.total} siswa tercatat
                                            </span>
                                        </span>
                                    </div>
                                </li>
                            )
                        })}
                    </ul>
                </div>
            </div>

            <div className='mt-2 flex gap-3' aria-hidden='true'>
                <div className='w-9 shrink-0' />

                <div className='flex min-w-0 flex-1 justify-around gap-2 sm:gap-4'>
                    {points.map((point, index) => (
                        <span
                            key={point.date}
                            className={cn(
                                'w-full max-w-16 text-center text-xs',
                                index === points.length - 1 ? 'font-semibold text-foreground' : 'font-medium text-muted-foreground'
                            )}
                        >
                            {formatDate(point.date, 'dayMonth')}
                        </span>
                    ))}
                </div>
            </div>

            <div className='mt-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t pt-3 text-xs text-muted-foreground'>
                <span className='inline-flex items-center gap-2 font-semibold text-foreground'>
                    <span className='size-2.5 rounded-full bg-primary' aria-hidden='true' />
                    Persentase siswa hadir
                </span>

                <span>
                    Data mengikuti tanggal absensi yang tersimpan
                </span>
            </div>
        </div>
    )
}
